import AxiosHelper from 'services/AxiosHelper';
import { BasePartialDataOptions } from 'shared/types';
import { BaseBulkPartialProductsDataOptions, ProductDetailsState } from './types';

const partialDataCache: { [index: string]: Partial<ProductDetailsState> } = {};
const partialProductsDataCache: { [index: string]: BulkProductsPartialDataResponse } = {};

export async function getPartialProductData<T extends ProductDetailsState = ProductDetailsState>(
	options: BasePartialDataOptions<T>,
) {
	let cacheKey = `${options.id}_${options.missingKeys.sort().join('_')}`;

	// include additional params in the cache key and preserve their order by param name
	const additionalParams = options.additionalParams;
	if (additionalParams) {
		const additionalKeys = Object.keys(additionalParams).sort();
		additionalKeys.forEach((name) => {
			const paramValue = additionalParams[name];
			if (paramValue) {
				cacheKey += `_${name}=${paramValue}`;
			}
		});
	}

	if (partialDataCache[cacheKey]) {
		return Promise.resolve(partialDataCache[cacheKey]);
	}

	const response = await AxiosHelper.get<Partial<ProductDetailsState>>(`/ajax/products/${options.id}`, {
		params: {
			...options.additionalParams,
			keys: options.missingKeys.join(','),
		},
	});

	partialDataCache[cacheKey] = response.data;

	// if the key is requested, always return the value as null. this is important
	// on the client undefined vs null can have different meanings
	// undefined means nothing has even tried to set it, the data is simply missing.
	// null means we tried to load something, but there's no value associated.
	return Promise.resolve(response.data);
}

/**
 * Dictionary of bulk products data , key is product id
 */
export interface BulkProductsPartialDataResponse {
	[key: string]: Partial<ProductDetailsState>;
}

/**
 * Gets partial data for bulk products using the product ids and the missing keys
 */
export async function getBulkPartialProductsData(options: BaseBulkPartialProductsDataOptions<ProductDetailsState>) {
	const cacheKey = `${options.productIds?.sort().join('_')}___${options.missingKeys.sort().join('_')}`;
	if (partialProductsDataCache[cacheKey]) {
		return Promise.resolve(partialProductsDataCache[cacheKey]);
	}

	const response = await AxiosHelper.post<BulkProductsPartialDataResponse>(`/ajax/products`, {
		productIds: options.productIds,
		missingKeys: options.missingKeys.join(','),
	});

	partialProductsDataCache[cacheKey] = response.data;

	// if the key is requested, always return the value as null. this is important
	// on the client undefined vs null can have different meanings
	// undefined means nothing has even tried to set it, the data is simply missing.
	// null means we tried to load something, but there's no value associated.
	return Promise.resolve(response.data);
}
